import React, { useState, useEffect, useRef } from 'react';
import { useIntersectionObserver } from './IntersectionObserver';

// Props for the animated counter component
interface AnimatedCounterProps {
  end: number;              // Target number to count to
  duration?: number;        // Animation duration in milliseconds
  className?: string;        // Additional CSS classes
  prefix?: string;          // Text before the number (e.g., "$")
  suffix?: string;          // Text after the number (e.g., "%")
}

/**
 * AnimatedCounter: Counts from 0 to a target number with smooth animation
 * Only animates when the element becomes visible in the viewport (using IntersectionObserver)
 */
const AnimatedCounter: React.FC<AnimatedCounterProps> = ({ 
  end, 
  duration = 2000, 
  className = "",
  prefix = "",
  suffix = ""
}) => {
  const [count, setCount] = useState(0);
  // IntersectionObserver hook: detects when element enters viewport
  const { ref, isIntersecting } = useIntersectionObserver();
  // useRef prevents animation from running multiple times
  const hasAnimated = useRef(false);

  useEffect(() => {
    // Only animate once when element becomes visible
    if (isIntersecting && !hasAnimated.current) {
      hasAnimated.current = true;
      let startTime: number;
      let animationFrame: number;

      // Animation loop using requestAnimationFrame for smooth 60fps animation
      const animate = (currentTime: number) => {
        if (!startTime) startTime = currentTime;
        // Calculate progress (0 to 1) based on elapsed time
        const progress = Math.min((currentTime - startTime) / duration, 1);
        
        // Update count based on progress
        setCount(Math.floor(progress * end));

        // Continue animation until complete
        if (progress < 1) {
          animationFrame = requestAnimationFrame(animate);
        }
      };

      animationFrame = requestAnimationFrame(animate);

      // Cleanup: cancel animation if component unmounts
      return () => {
        if (animationFrame) {
          cancelAnimationFrame(animationFrame);
        }
      };
    }
  }, [isIntersecting, end, duration]);

  return (
    <span ref={ref} className={className}>
      {prefix}{count}{suffix}
    </span>
  );
};

export default AnimatedCounter; 